import { useFieldArray, UseFormReturn } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DOCUMENT_CATEGORIES } from '../../../documents.types'
import { DocumentUploadFormData } from '../../../documents-upload.types'
import { MarkdownEditor } from '../../markdown-editor'
import { TagsInput } from '../../tags-input'
import { useFetchTags } from '../../../hooks/useDocuments'
import { useFetchFolders } from '../../../hooks/useFolders'

const FIELD_CLASS = 'h-10 rounded-lg bg-background text-sm shadow-sm border border-border'

type DetailsStepProps = {
  form: UseFormReturn<DocumentUploadFormData>
}

export const DetailsStep = ({ form }: DetailsStepProps) => {
  const { fields } = useFieldArray({ control: form.control, name: 'files' })
  const { register, formState } = form
  const errors = formState.errors
  const { data: tagFacets, isLoading: tagsLoading } = useFetchTags()
  const { data: folders = [], isLoading: foldersLoading } = useFetchFolders()

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-lg font-semibold">Details</h3>
        <p className="text-sm text-muted-foreground">
          These details apply to every file in this batch — each file keeps its own title.
        </p>
      </div>

      {fields.length > 1 && (
        <div className="flex flex-col gap-2">
          <Label>File titles</Label>
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-col gap-1">
              <Input {...register(`files.${index}.title`)} className={FIELD_CLASS} />
              {errors.files?.[index]?.title && (
                <p className="text-xs text-destructive">{errors.files[index]?.title?.message}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {fields.length === 1 && (
        <div className="flex flex-col gap-1.5">
          <Label>Title</Label>
          <Input {...register('files.0.title')} className={FIELD_CLASS} />
          {errors.files?.[0]?.title && (
            <p className="text-xs text-destructive">{errors.files[0]?.title?.message}</p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label>Description</Label>
        <MarkdownEditor
          rows={3}
          placeholder="Short description"
          value={form.watch('descriptionMd') ?? ''}
          onChange={(value) => form.setValue('descriptionMd', value, { shouldDirty: true })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Category</Label>
          <Select
            value={form.watch('category')}
            onValueChange={(value) =>
              // Only ever populated from DOCUMENT_CATEGORIES below, so always a real category.
              form.setValue('category', value as DocumentUploadFormData['category'], {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          >
            <SelectTrigger className={FIELD_CLASS}>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_CATEGORIES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && <p className="text-xs text-destructive">{errors.category.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Date published (if applicable)</Label>
          <Input type="date" {...register('publishedAt')} className={FIELD_CLASS} />
          {errors.publishedAt && (
            <p className="text-xs text-destructive">{errors.publishedAt.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Source</Label>
          <Input
            placeholder="e.g. Christchurch City Council"
            className={FIELD_CLASS}
            {...register('source')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Organisation</Label>
          <Input
            placeholder="e.g. Christchurch City Council"
            className={FIELD_CLASS}
            {...register('organisation')}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Tags</Label>
        <TagsInput
          value={form.watch('tags')}
          onChange={(tags) => form.setValue('tags', tags, { shouldDirty: true })}
          existingTags={(tagFacets ?? []).map((facet) => facet.tag)}
          isLoading={tagsLoading}
        />
        <p className="text-xs text-muted-foreground">
          Add keywords so others can find this document. Search existing tags or create a new one.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Add to Collection</Label>
        <Select
          value={form.watch('collectionId') ?? 'none'}
          onValueChange={(value) =>
            form.setValue('collectionId', value === 'none' ? null : value, { shouldDirty: true })
          }
        >
          <SelectTrigger className={FIELD_CLASS} disabled={foldersLoading}>
            <SelectValue placeholder="No collection" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No collection</SelectItem>
            {folders.map((folder) => (
              <SelectItem key={folder.id} value={folder.id}>
                {folder.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Notes</Label>
        <MarkdownEditor
          rows={2}
          placeholder="Internal notes"
          value={form.watch('notesMd') ?? ''}
          onChange={(value) => form.setValue('notesMd', value, { shouldDirty: true })}
        />
      </div>
    </div>
  )
}
